"use strict";
import { sendErrorHubEvent } from "./my-error-hub.min.js?v=1792136343043";
import { ERROR_HUB_EVENTS } from "./my-error-hub-events.min.js?v=1792136343043";
import {
  CART_SECTION_ERROR_SCENARIOS,
  CART_STATE_ERROR_SCENARIOS,
  themeRequest,
} from "./my-theme-request.min.js?v=1792136343043";
import { fetchCartMutation } from "./my-cart-api.min.js?v=1792136343043";
import { CART_STATE_ELEMENT_IDS } from "./my-cart-section-registry.min.js?v=1792136343043";
import {
  buildCommerceEnvironmentContext,
  buildShortDiagnosticHash,
} from "./my-commerce-diagnostics.min.js?v=1792136343043";
export { sendErrorHubEvent };
/* =======================================
 get device
======================================= */
export function getDevice() {
  if (document.body.clientWidth <= 750) {
    return 'sp';
  } else {
    return 'pc';
  }
}
/* =======================================
 get current language
======================================= */
export function getCurrentLanguage() {
  const path = new URL(location.href).pathname;
  // ドメイン直下のパスを抽出
  const languagePath = path.split('/')[1];
  if (languagePath === '') {
    return 'ja'; // 日本語の場合は例外処理
  } else {
    // 言語用パスから言語を判別
    const language = languagePath.toLowerCase();
    if (language === 'en') {
      return 'en';
    } else if (language === 'zh') {
      return 'zh';
    } else if (language === 'zh-tw') {
      return 'zh-tw';
    } else if (language === 'ko') {
      return 'ko';
    } else {
      return 'ja'; // 言語用パスが該当しない場合はデフォルトとして日本語とする
    }
  }
}
/* =================================================================== */

export function getCookieValue(key) {
  const cookieArray = document.cookie.split(';');
  for (const entry of cookieArray) {
    const [cookieKey, value] = entry.trim().split('=');
    if (cookieKey === key) {
      return value;
    }
  }
  return null; // 該当するクッキーが見つからなかった場合
}
function _customApiDebugLog(...args) {
  try {
    if (window.__CUSTOM_API_DEBUG__ === true) {
      console.log(...args);
      return;
    }
    const search = String(window.location?.search || '');
    if (/(?:\?|&)(?:cart_debug|custom_api_debug)=1(?:&|$)/.test(search)) {
      console.log(...args);
    }
  } catch (error) {
    // ignore
  }
}
function _isFetchNetworkFailure(error) {
  return error instanceof TypeError;
}
function _getCartDiagnosticsCorrelationId() {
  return getSharedCorrelationId();
}
function _buildSectionCommunicationContext(context = {}) {
  const normalizedContext = context && typeof context === 'object' ? context : {};
  const cartData = window.__CART_DATA__ || null;
  const cartItems = Array.isArray(cartData?.items) ? cartData.items : [];
  return buildCommerceEnvironmentContext({
    cartItemCountHint: cartItems.length,
    cartTokenHash: buildShortDiagnosticHash(cartData?.token || cartData?.key || ''),
    correlationId: _getCartDiagnosticsCorrelationId(),
    cartLineIdsHint: cartItems.map((item) => String(item?.id || item?.variant_id || item?.product_id || '')).filter(Boolean),
    cartAttributeKeysHint: cartData?.attributes ? Object.keys(cartData.attributes) : null,
    ...normalizedContext,
  });
}
function _buildFetchFailureDiagnostics(error, context = {}) {
  const normalizedContext = _buildSectionCommunicationContext(context);
  const message = String(error?.message || error || 'Unknown error');
  const name = String(error?.name || 'Error');
  const online = typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
    ? navigator.onLine
    : null;
  const connection = navigator?.connection || navigator?.mozConnection || navigator?.webkitConnection || null;
  const connectionType = connection?.effectiveType || connection?.type || null;
  const downlink = Number.isFinite(Number(connection?.downlink)) ? Number(connection.downlink) : null;
  const rtt = Number.isFinite(Number(connection?.rtt)) ? Number(connection.rtt) : null;
  const saveData = typeof connection?.saveData === 'boolean' ? connection.saveData : null;
  const aborted = error?.name === 'AbortError';
  let inferredLayer = 'unknown';
  if (aborted) {
    inferredLayer = 'client-abort-or-timeout';
  } else if (_isFetchNetworkFailure(error)) {
    inferredLayer = 'network-transport';
  }
  return {
    errorName: name,
    errorMessage: message,
    inferredLayer,
    aborted,
    isTypeError: error instanceof TypeError,
    online,
    connectionType,
    connectionDownlinkMbps: downlink,
    connectionRttMs: rtt,
    connectionSaveData: saveData,
    ...normalizedContext,
  };
}
async function _retryOnceOnFetchFailure(task) {
  try {
    return await task();
  } catch (error) {
    if (!_isFetchNetworkFailure(error)) {
      throw error;
    }
    return task();
  }
}
async function _notifyCriticalSectionCommunicationError(source, title, error, extra = {}, phase = 'unknown') {
  try {
    const diagnostics = _buildFetchFailureDiagnostics(error, extra);
    const normalizedPhase = String(phase || 'unknown').trim() || 'unknown';
    const details = {
      ...diagnostics,
      phase: normalizedPhase,
      reasonCode: String(diagnostics.reasonCode || diagnostics.failureKind || 'section-communication-error'),
    };
    void sendErrorHubEvent({
      source: String(source || 'unknown').trim() || 'unknown',
      kind: 'error',
      title: 'Section communication error',
      message: 'Section communication error occurred.',
      correlationId: String(details?.correlationId || ''),
      details,
    });
  } catch (reportError) {
    console.warn('[error-hub] failed to notify critical section communication error', reportError);
  }
}
/* =================================================================== */

export class SwipeEvent {
  constructor(
    {
      target = document,
      onSwipeStart,
      onSwipeMove,
      onSwipeMoveR,
      onSwipeMoveL,
      onSwipeMoveEsc,
      onSwipeEnd,
      onSwipeEndR,
      onSwipeEndL,
      dist = 40
    }
  ) {
    if (!target) {
      console.error('target must be specified');
      return;
    }
    this.target = target;
    this.posStartX; // 始点X座標
    this.posMoveX; // 移動後X座標
    this.dist = dist; // 移動感知最短距離
    this.onSwipeStart = onSwipeStart;
    this.onSwipeMove = onSwipeMove;
    this.onSwipeMoveR = onSwipeMoveR;
    this.onSwipeMoveL = onSwipeMoveL;
    this.onSwipeMoveEsc = onSwipeMoveEsc;
    this.onSwipeEnd = onSwipeEnd;
    this.onSwipeEndR = onSwipeEndR;
    this.onSwipeEndL = onSwipeEndL;
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    // イベントリスナー登録 タップ開始
    this.target.addEventListener("touchstart", this.handleTouchStart, { passive: true });
    // タップ中
    this.target.addEventListener("touchmove", this.handleTouchMove, { passive: true });
    // タップエンド
    this.target.addEventListener("touchend", this.handleTouchEnd, { passive: true });
  }
  removeEvent() {
    this.target.removeEventListener("touchstart", this.handleTouchStart, false);
    this.target.removeEventListener("touchmove", this.handleTouchMove, false);
    this.target.removeEventListener("touchend", this.handleTouchEnd, false);
  }
  // 開始時イベント
  handleTouchStart(e) {
    this.posStartX = e.touches[0].pageX; // 始点X
    this.posMoveX = this.posStartX; // 前動作の数値を参照しないようリセット
    if (this.onSwipeStart) this.onSwipeStart(e);// constructor定義済みなら実行
  }
  // 移動時イベント
  handleTouchMove(e) {
    this.posMoveX = e.changedTouches[0].pageX;
    if (this.onSwipeMove) this.onSwipeMove(e);
    if (this.posStartX > this.posMoveX + this.dist) {
      // 右から左にスワイプ中
      if (this.onSwipeMoveR) this.onSwipeMoveR(e);
    } else if (this.posStartX + this.dist < this.posMoveX) {
      // 左から右にスワイプ中
      if (this.onSwipeMoveL) this.onSwipeMoveL(e);
    } else {
      // スワイプ無効
      if (this.onSwipeMoveEsc) this.onSwipeMoveEsc(e);
    }
  }
  // 指を放した時
  handleTouchEnd(e) {
    if (this.onSwipeEnd) this.onSwipeEnd(e);
    if ((this.posStartX > this.posMoveX) && (this.posStartX > this.posMoveX + this.dist)) {
      // 右から左にスワイプ
      if (this.onSwipeEndR) this.onSwipeEndR(e);
    } else if ((this.posStartX < this.posMoveX) && (this.posStartX + this.dist < this.posMoveX)) {
      // 左から右にスワイプ
      if (this.onSwipeEndL) this.onSwipeEndL(e);
    }
  }
}
/* =================================================================== */

export class EasySlider {
  constructor(
    {
      target,
      margin = 10,
      slidesToScroll = null,
      itemsPerViewSp = 1,
      itemsPerViewPc = 1,
      isAutoLoop = false,
      loopIntarval = 6500,
      prevArrow = null,
      nextArrow = null,
      dots = true,
      centerMode = false
    }
  ) {
    // 定義要素
    this.settings = {
      target: target,
      children: target.children,
      margin: margin,
      slidesToScroll: slidesToScroll,
      itemsPerViewSp: itemsPerViewSp,
      itemsPerViewPc: itemsPerViewPc,
      itemsPerView: itemsPerViewSp,
      isAutoLoop: isAutoLoop,
      loopIntarval: loopIntarval,
      prevArrow: prevArrow,
      nextArrow: nextArrow,
      dots: dots,
      centerMode: centerMode,
    };
    // 処理用
    this.wrapper; // ラッパー要素
    this.dotsElm; // ドット要素
    this.scrollEndTimer = null; // スクロール完了を検知するタイマーID名
    this.dummyImgSrc = 'https://cdn.shopify.com/s/files/1/0593/3613/0738/files/dummy.gif';
    this.intervalId = null; // オートスクロールにおける、インターバルのIDを保持するプロパティ
    // itemsPerViewを元にした、スライダー要素の表示段階
    this.currentSlideIndex = 0;
    this.init();
  }
  init() {
    this.setupBody();
    this.setupItemsPerView(); // 必ずsetupDotsの前に実行する
    this.setupBtn();
    this.setupDots();
    this.setupSwipe();
    this.loadImagesInView(0);
    this.settings.target.addEventListener("scroll", this.onScroll.bind(this));
    let currentWidth = 0;
    const resetDots = {
      target: this.settings.target,
      event: () => {
        if (window.innerWidth !== currentWidth) {
          currentWidth = window.innerWidth;
          if (this.dotsElm) {
            this.wrapper.removeChild(this.dotsElm);
            this.dotsElm = null;
          }
          this.setupItemsPerView();
          this.setupDots();
        }
      },
      timeout: 500
    };
    const resizeObserver = new ResizeObserver(resetDots);
    if (this.settings.centerMode) {
      this.settings.children[0].style.marginLeft = `${this.slideTerm}px`;
      this.settings.children[this.totalDots - 1].style.marginRight = `${this.slideTerm}px`;
    }
    if (this.settings.isAutoLoop) {
      this.setAutoSlide();
    }
  }
  
  get totalDots() {
    // 表示数とスクロール数に応じた、1スライドにおける表示数を計算
    const scrollNum = Math.ceil(this.settings.itemsPerView * this.scrollPercentage);
    // 全点数をもとに、ドットの最大数を計上
    return Math.ceil(this.settings.children.length / scrollNum);
  }
  get scrollPercentage() {
    // スクロール数/表示数 同数なら等倍、異なれば調整数値を返す
    return this.settings.slidesToScroll / this.settings.itemsPerView;
  }
  get slideTerm() {
    // スライド要素1つの横幅及び、余白マージン値の合算値
    const slideElmsWidth = this.settings.children[0].clientWidth + this.marginTerm;
    // スライド要素幅 * 表示数 * スクロール割合
    return (slideElmsWidth * this.settings.itemsPerView * this.scrollPercentage);
  }
  get marginTerm() {
    // 要素の横幅 * 指定％
    return this.settings.target.clientWidth * this.settings.margin / 100;
  }
  get isLastNum() {
    // 要素内最大までスクロールしたか
    const isReachEnd = () => {
      // 現在のスクロール位置
      const scrollLeft = this.settings.target.scrollLeft;
      // 要素の全体のスクロール幅
      const scrollWidth = this.settings.target.scrollWidth;
      // 要素のビューポート幅
      const clientWidth = this.settings.target.clientWidth;
      // 小数点の位でずれが生じるため、誤差を設定
      return scrollLeft + clientWidth >= scrollWidth - 2;
    }
    // 小数点の位でずれが生じるため、誤差を設定
    return isReachEnd();
  }
  setupBody() {
    // this.settings.target.classList.add('easyslider__scroller'); // レイアウト用css
    // 新たなラッパーに格納する
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('is-easyslider');
    this.settings.target.parentNode.insertBefore(this.wrapper, this.settings.target);
    this.wrapper.appendChild(this.settings.target);
    // css変数をセット
    this.wrapper.style.setProperty('--margin', `${this.settings.margin}%`);
  }
  setupItemsPerView() {
    if (getDevice() === 'pc') {
      this.settings.itemsPerView = this.settings.itemsPerViewPc;
    } else if (getDevice() === 'sp') {
      this.settings.itemsPerView = this.settings.itemsPerViewSp;
    }
    // スクロール数の指定が無ければ、同数で定義
    if (!this.settings.slidesToScroll) {
      this.settings.slidesToScroll = this.settings.itemsPerView;
    }
    // css変数をセット
    this.wrapper.style.setProperty('--itemStep', this.settings.itemsPerView);
  }
  setupBtn() {
    // 規定の個数以下ならボタン不要
    if (this.settings.children.length > this.settings.itemsPerView) {
      if (this.settings.prevArrow && this.settings.nextArrow) {
        // ボタンタグの指定があれば
        const parseHTML = (html) => {
          const template = document.createElement("template");
          template.innerHTML = html.trim();
          return template.content.firstChild;
        }
        const prevButton = parseHTML(this.settings.prevArrow);
        const nextButton = parseHTML(this.settings.nextArrow);
        prevButton.addEventListener("click", () => this.targetScroll("prev"));
        nextButton.addEventListener("click", () => this.targetScroll("next"));
        this.wrapper.appendChild(prevButton);
        this.wrapper.appendChild(nextButton);
        // プロパティを更新
        this.settings.prevArrow = prevButton;
        this.settings.nextArrow = nextButton;
      } else {
        const buttonData = [
          { className: "easyslider-btn easyslider-prev is-disabled", text: "prev" },
          { className: "easyslider-btn easyslider-next", text: "next" },
        ];
        buttonData.forEach(data => {
          // ボタン要素を作成する
          const button = document.createElement("button");
          // ボタンのクラス名を設定する
          button.className = data.className;
          // テキストを追加する
          button.innerText = data.text;
          // クリックイベントを追加する
          button.addEventListener("click", () => this.targetScroll(data.text));
          // this.settings.targetにボタン要素を追加する
          this.wrapper.appendChild(button);
          // プロパティを更新
          if (data.text === 'next') {
            this.settings.nextArrow = button;
          } else if (data.text === 'prev') {
            this.settings.prevArrow = button;
          }
        });
      }
    }
  }
  setupDots() {
    // ドット追加
    if ((this.settings.children.length > this.settings.itemsPerView) && this.settings.dots === true) {
      // 規定の個数より大きい時、引数trueのみ
      this.dotsElm = document.createElement("ol");
      this.dotsElm.classList.add('easyslider__dots');
      // イベントリスナー登録
      for (let i = 1; i <= this.totalDots; i++) {
        const li = document.createElement("li");
        li.innerText = i;
        li.addEventListener('click', () => {
          this.targetScroll(null, parseInt(i) - 1);
          this.loadImagesInView(parseInt(i));
        });
        this.dotsElm.appendChild(li);
      }
      // ドットをレンダリング
      this.wrapper.appendChild(this.dotsElm);
      this.dotsElm.children[0].classList.add('is-active');
    }
  }
  setupSwipe() {
    // スワイプ用classを定義
    const swipeEndR = (e) => {
      this.targetScroll('next');
    }
    const swipeEndL = (e) => {
      this.targetScroll('prev');
    }
    const onSwipe = new SwipeEvent(
      {
        target: this.settings.target,
        dist: 1,
        onSwipeEndR: swipeEndR,
        onSwipeEndL: swipeEndL
      }
    );
  }
  loadImagesInView(num) {
    // this.settings.itemsPerViewをもとに、次のスライド先枚数も読み込む
    const startIndex = num * this.settings.slidesToScroll; // 表示開始数 * 一度の表示数
    const endIndex = startIndex + this.settings.itemsPerView - 1; // 上記に、PCおよびSPでの表示数、index数のため-1で調整
    // console.log('number', startIndex, endIndex, this.settings.slidesToScroll);
    // numが上限枚数を越えたなら、これ以上不要
    if (num > this.totalDots - 1) {
      return false;
    }
    // 子要素に対して処理
    [...this.settings.children].forEach((elm, index) => {
      if (index >= startIndex && index <= endIndex) {
        // 指定番号+（表示枚数 *2）まで かつ、srcsetが未セットなら
        const childImg = elm.querySelector('img');
        if (childImg && !childImg.hasAttribute('srcset')) {
          childImg.srcset = childImg.dataset.srcset;
        }
      }
    });
  }
  // スクロール完了を識別
  onScroll() {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      // スクロール停止後に実行
      this.updateSlideStatus();
    }, 100);
  }
  // スクロール位置に応じた状態変化
  updateSlideStatus() {
    // console.log('update start', this.currentSlideIndex, this.isLastNum, this.totalDots);
    // ボタンのクリック可・不可状態
    if (this.currentSlideIndex === 0) {
      this.settings.prevArrow.classList.add("is-disabled");
      this.settings.nextArrow.classList.remove("is-disabled");
    } else if (this.isLastNum) {
      this.settings.prevArrow.classList.remove("is-disabled");
      this.settings.nextArrow.classList.add("is-disabled");
    } else {
      this.settings.prevArrow.classList.remove("is-disabled");
      this.settings.nextArrow.classList.remove("is-disabled");
    }
    // ドットのアクティブ
    const changeActiveDot = (num) => {
      // 現在アクティブなドットを外す
      const currentActive = this.dotsElm.querySelector('.is-active');
      currentActive.classList.remove('is-active');
      this.dotsElm.children[num].classList.add('is-active');
    }
    if (this.isLastNum) {
      // スクロール不可能なら最後のドットをアクティブ（個数 - 1でindex値に調整）
      if (this.settings.dots) changeActiveDot(this.totalDots - 1);
    } else {
      if (this.settings.dots) changeActiveDot(this.currentSlideIndex);
    }
    // 画像遅延読み込み
    this.loadImagesInView(this.currentSlideIndex);
  }
  
  targetScroll(order, number) {
    const slideTarget = (px) => {
      this.settings.target.scrollTo({
        left: px,
        behavior: "smooth"
      });
    }
    if (number != null) {
      const scrollPx = Math.ceil(this.slideTerm * (number));
      slideTarget(scrollPx);
      this.currentSlideIndex = number;
    } else if (order === 'prev' && this.currentSlideIndex !== 0) {
      // prevボタンは現在値が0でなければ
      const scrollPx = Math.ceil(this.slideTerm * (this.currentSlideIndex - 1));
      slideTarget(scrollPx);
      this.currentSlideIndex -= 1;
    } else if (order === 'next' && !this.isLastNum) {
      // nextボタンは、最後の値でなければ
      // nextに限っては若干ピクセルがずれるため調整
      const scrollPx = Math.ceil(this.slideTerm * (this.currentSlideIndex + 1) + 1);
      slideTarget(scrollPx);
      this.currentSlideIndex += 1;
    }
  }
  // 自動スクロール開始
  setAutoSlide() {
    const onComplete = () => {
      if (this.isLastNum) {
        this.currentSlideIndex = 0;
        this.targetScroll('prev', 0);
      } else {
        this.targetScroll('next');
      }
    };
    // setIntervalを用いたタイマーを起動 インターバル6.5秒
    const counter = new Counter(6500, onComplete);
    counter.startCountUp();
    // 要素のマウスオーバー、またはスワイプ時は停止
    this.settings.target.addEventListener('mouseenter', () => {
      counter.stop();
    });
    this.settings.target.addEventListener('touchstart', () => {
      counter.stop();
    });
    // マウスアウト、スワイプアウトで再開
    this.settings.target.addEventListener('mouseleave', () => {
      counter.resumeCountUp();
    });
    const counterReset = () => {
      counter.reset();
      counter.resumeCountUp();
    }
    this.settings.target.addEventListener('touchend', () => {
      // タッチの場合、次のスライドに移動している可能性を考慮し、カウント数リセット後再開
      counterReset();
    });
    // 各操作ボタンに、クリック時カウントリセットを付与
    // ボタンが存在していれば setupBtnと同じ条件
    if (this.settings.children.length > this.settings.itemsPerView) {
      this.settings.prevArrow.addEventListener('click', () => counterReset());
      this.settings.nextArrow.addEventListener('click', () => counterReset());
    }
    // ドットが存在していれば setupDotsと同じ条件
    if ((this.settings.children.length > this.settings.itemsPerView) && this.settings.dots === true) {
      Array.from(this.dotsElm.children).forEach(li => {
        li.addEventListener('click', () => counterReset());
      });
    }
  }
}
/* =================================================================== */

export class CustomIntersectionObserver extends IntersectionObserver {
  constructor({ target, onEnter = null, onLeave = null, onceOnEnter = true, onceOnLeave = true }) {
    // targetが存在しない場合はエラーを出力して処理を中断する
    if (!target) {
      console.error(`[${this.constructor.name}] Target element is not specified`);
      return;
    }
    // 親クラスの constructor を呼び出して IntersectionObserver を初期化
    super((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          // 監視対象が画面外に出た場合
          if (onLeave && (!onceOnLeave || this.leaveCallbackCount === 0)) {
            this.leaveCallbackCount++;
            onLeave(entries, this);
          }
        } else {
          // 監視対象が画面内に入った場合
          if (onEnter && (!onceOnEnter || this.enterCallbackCount === 0)) {
            this.enterCallbackCount++;
            onEnter(entries, this);
          }
        }
      });
      // 実行回数が1回なら監視解除
      if (onceOnEnter && onceOnLeave && this.enterCallbackCount === 1 && this.leaveCallbackCount === 1) {
        observer.disconnect();
      }
    }, { once: onceOnEnter });
    // 監視対象の要素を指定して IntersectionObserver を初期化
    this.targetElement = target;
    this.observe(this.targetElement);
    // 任意に呼び出すための、監視解除メソッド
    this.disconnect = () => {
      if (this.targetElement) {
        this.unobserve(this.targetElement);
      }
    };
    // 実行回数を計上
    this.enterCallbackCount = 0;
    this.leaveCallbackCount = 0;
  }
}

export class ResizeObserver {
  constructor(options) {
    const { target, event, timeout = 500 } = options;
    let resizeTimer;
    let currentWidth = target.clientWidth;
    const resizeObserverCallback = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const newWidth = target.clientWidth;
        if (newWidth !== currentWidth) {
          currentWidth = newWidth;
          event(target);
        }
      }, timeout);
    };
    const handleResize = () => {
      resizeObserverCallback();
    };
    window.addEventListener('resize', handleResize);
    // 任意に呼び出すための、監視解除メソッド
    this.disconnect = () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', handleResize);
    };
  }
}
/* =================================================================== */

export const getProductCardsArray = async (productId, variantId) => {
  const url = `/products/${productId}.js`;
  const resizeImg = (src, size = '110') => {
    const sizeText = `_${size}x${size}@2x.`;
    let parts = src.split("/");
    let [filename, extension] = parts[parts.length - 1].split(".");
    let thumbFilename = filename + sizeText + extension;
    return parts.slice(0, -1).join("/") + "/" + thumbFilename;
  }
  try {
    // fetchが完了したらバリアント情報を格納
    const response = await fetch(url);
    const data = await response.json();
    const variants = data.variants;
    const targetVariant = variants.find(variant => variant.id === variantId);
    if (targetVariant) {
      const item = {};
      item.image = resizeImg(targetVariant.featured_image.src);
      item.title = data.title;
      item.vendor = data.vendor;
      item.option1 = targetVariant.option1;
      item.option2 = targetVariant.option2;
      item.url = `${data.url}?variant=${variantId}`;
      item.price = targetVariant.price;
      item.compare_at_price = targetVariant.compare_at_price;
      item.compare_at_price_varies = data.compare_at_price_varies;
      item.available = targetVariant.available;
      item.tags = [];
      if (data.tags.includes("specialprice")) {
        // バッジ「特別価格」
        const pushObj = {
          tagName: "specialprice",
          dispName: "特別価格"
        };
        item.tags.push(pushObj);
      }
      if (data.tags.includes("fair-出産準備フェア")) {
        const pushObj = {
          tagName: "childbirth",
          dispName: "出産準備フェア"
        };
        item.tags.push(pushObj);
      }
      if (data.tags.includes("eギフト")) {
        const pushObj = {
          tagName: "e_gift",
          dispName: "eギフト"
        };
        item.tags.push(pushObj);
      }
      return item;
    } else {
      return null;
    };
  } catch (error) {
    console.error(`[getProductCardsArray] Error occurred while fetching JSON data: Failed to fetch /products/${productId}.js`);
    return null;
  }
};
/* =================================================================== */

export function createProductCardHtml(variantObject, dummyImage) {
  const orderKeys = ["image", "vendor", "title", "compare_at_price", "tags"];
  let productCardHTML = "";
  orderKeys.forEach(key => {
    if (variantObject[key] !== undefined && variantObject[key] !== null) {
      let tag;
      switch (key) {
        case 'image':
          if (dummyImage === true) {
            tag = `<div class="img"><img src="https://cdn.shopify.com/s/files/1/0593/3613/0738/files/dummy.gif" data-srcset="${variantObject[key]}" alt="${variantObject.title}"></div>`;
          } else {
            tag = `<div class="img"><img src="${variantObject[key]}" alt="${variantObject.title}" data-test></div>`;
          }
          break;
        case 'vendor':
          tag = `<p class="vendor">${variantObject[key]}</p>`;
          break;
        case 'title':
          tag = `<p class="name">${variantObject[key]}</p>`;
          break;
        case 'compare_at_price':
          let compare_at_price = `&yen;${(variantObject.compare_at_price / 100).toLocaleString("ja-JP")}`;
          let displayPrice = '';
          if (variantObject.compare_at_price_varies) {
            compare_at_price += '～';
          }
          tag = `<p class="price">${compare_at_price}<span class="tax">（税込）</span></p>`;
          if (variantObject.compare_at_price !== variantObject.price) {
            displayPrice = `&yen;${(variantObject.price / 100).toLocaleString("ja-JP")}`;
            if (variantObject.compare_at_price_varies) {
              displayPrice += '～';
            }
            tag = `<p class="price"><span class="sale">${displayPrice}</span><del>${compare_at_price}</del><span class="tax">（税込）</span></p>`;
          }
          break;
        case 'tags':
          let campaignHTML = "";
          variantObject.tags.forEach(tag => {
            if (tag) {
              campaignHTML += `<span class="badge badge--${tag.tagName}">${tag.dispName}</span>`;
            }
          });
          if (campaignHTML) tag = `<div class="campaign">${campaignHTML}</div>`;
          break;
        default:
          break;
      }
      if (tag) {
        productCardHTML += tag;
      }
    }
  });
  return `
    <li class="grid__item is-show">
      <a href="${variantObject.url}">
        ${productCardHTML}
      </a>
    </li>
  `;
}
/* =================================================================== */

export class TabSwitcher {
  constructor({ targets }) {
    this.targets = targets; // 切り替え対象ラッパー
    this.elements = targets.querySelectorAll('.js-tabswitch__target'); // 切り替え対象要素群
    this.buttonElements = targets.querySelectorAll('.js-tabswitch__buttons button'); // 切り替えボタン群
    // 初期化処理
    this.setup();
  }
  setup() {
    // 最初の切り替え要素、画像を正式化
    this.updateImageSrc(this.elements[0]);
    // タブボタンにイベントリスナー追加
    this.buttonElements.forEach((button) => {
      button.addEventListener('click', this.onClickButtons.bind(this));
    });
  }
  /*
    * 切替要素、ダミー画像を正式化
    * param{Element} container 適用ラッパー要素
    */
  updateImageSrc(container) {
    const imgElements = container.querySelectorAll("img");
    imgElements.forEach((imgElement) => {
      if (imgElement.src === 'https://cdn.shopify.com/s/files/1/0593/3613/0738/files/dummy.gif') {
        const dataSrc = imgElement.getAttribute("data-src");
        imgElement.setAttribute("src", dataSrc);
      }
    });
  }
  
  onClickButtons = (event) => {
    if (event.target.classList.contains('is-active')) return; // すでに active なら処理しない
    // クリックされたタブボタンの data 属性の数値を取得（キャッシュ）
    const buttonValue = parseInt(event.target.dataset.tabbutton);
    // すでに `is-active` の要素を取得し、削除
    const currentActiveElement = this.targets.querySelector('.tabswitch__container.is-active');
    if (currentActiveElement) currentActiveElement.classList.remove('is-active');
    const currentActiveButton = this.targets.querySelector('button.is-active');
    if (currentActiveButton) currentActiveButton.classList.remove('is-active');
    // 新しいアクティブ要素を設定
    this.elements[buttonValue].classList.add('is-active');
    this.buttonElements[buttonValue].classList.add('is-active');
    // ラッパー要素のアクティブ属性を更新
    this.targets.setAttribute('data-active_tab', buttonValue);
    // 画像の更新
    this.updateImageSrc(this.elements[buttonValue]);
  }
}
/* =================================================================== */

export class ShopifyCartHandler {
  constructor() {
    /*
      送信データ 以下形式を追加していく
      {
        'id': {Number}Variant ID,
        'quantity': {Number}
      },{}...
     */
    this.sendData = {
      'items': []
    };
    /* カート アップデートオブジェクト
      {
        794864053: 2,
        794864233: 3
        商品ID: 個数（0なら削除処理）
      };
    */
    this.updates = {};
    // カートの内容
    this.cartJson = null;
    // カート内商品点数表示要素 ラッパー要素
    this.cartItemCountWrap = document.getElementById('cart-icon-bubble');
  }
  resetSendData() {
    this.sendData = {
      'items': []
    };
  }
  appendSendData(itemId, quantity = 1, properties = null) {
    const item = {
      id: itemId,
      quantity: quantity
    };
    if (properties && Object.keys(properties).length > 0) {
      item.properties = properties;
    }
    this.sendData.items.push(item);
    this.sendData.sections = [
      "cart-notification-product",
      "cart-notification-button",
      "cart-icon-bubble"
    ];
  }
  async addToCart() {
    try {
      const response = await fetchCartMutation({
        order: 'add',
        body: this.sendData,
        target: 'cart-generic-add',
      });
      const data = await response.json();
      console.log('Success', data);
      /*
       通信成功時 dataの形式
       ・カート追加成功
        "items": [
          {
            "id": 40994174861464,
            "quantity": 1,
            "variant_id": 40994174861464,
            "title": "お花レース7分丈カバーオール - 白 / 80cm",
            "price": 1078000,
            ...（追加した商品情報）
          },
        ]
       ・失敗時（必ずしも在庫が無い時などに限定せず、多岐にわたる）
        {
          "status": 422,
          "message": "カートのエラー",
          "description": "2個の6層織りガーゼ素材のベビーバスローブ - アイボリー / フリー(70-90cm)がカートに入っています。"
        }
      */
      return data;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  }
  updateCartNotification(result) {
    if (!result.sections || typeof result.sections !== 'object') return;
    const itemIds = result.items.map(item => item.id);
    Object.entries(result.sections).forEach(([sectionId, htmlContent]) => {
      const targetElement = document.getElementById(`${sectionId}`);
      if (!targetElement) return;
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlContent;
      if (sectionId === "cart-notification-product") {
        const cartItems = tempDiv.querySelectorAll('.cart-item');
        cartItems.forEach(cartItem => {
          const itemIdFromHtml = cartItem.id.split(':')[0].split('-').pop(); // id部分を取得
          if (itemIds.includes(Number(itemIdFromHtml))) {
            targetElement.innerHTML = cartItem.innerHTML;
          }
        });
      } else {
        const newContentElement = tempDiv.querySelector('.shopify-section');
        if (newContentElement) {
          targetElement.innerHTML = newContentElement.innerHTML;
        }
      }
    });
    document.getElementById('cart-notification').classList.add('active');
  }
  appendUpdateData(itemId, quantity = 0) {
    this.updates.push({
      [itemId]: quantity
    });
  }
  async updateToCart() {
    try {
      const response = await fetchCartMutation({
        order: 'update',
        body: this.updates,
        target: 'cart-generic-update',
      });
      const data = await response.json();
      console.log('Success', data);
      return data;
    } catch (error) {
      console.error('Error:', error);
      throw error;
    }
  }
  async fetchCartData() {
    try {
      const cartDataResponse = await themeRequest({
        target: 'cart-state-read',
        request: () => fetch(window.Shopify.routes.root + 'cart.js'),
      });
      this.cartJson = await cartDataResponse.json();
      return this.cartJson;
    } catch (error) {
      console.error('Error fetching cart data:', error);
      throw error;
    }
  }
  async updateCartItemCount() {
    try {
      // cart.js からデータを取得、情報を更新する
      await this.fetchCartData();
      if (!this.cartJson) {
        console.error('Cart data not available.');
        return;
      }
      // カート内商品点数を取得し、表示を更新
      const cartItemCount = this.cartJson.item_count || 0;
      ShopifyCartHandler.updateCartItemCountDom(cartItemCount);
    } catch (error) {
      console.error('Error updating cart item count:', error);
    }
  }
  
  static updateCartItemCountDom(cartItemCount) {
    const cartItemCountWrap = document.getElementById('cart-icon-bubble');
    if (!cartItemCountWrap) return;
    // 既存のバブル要素を取得
    const cartCountBubble = cartItemCountWrap.querySelector('.cart-count-bubble');
    if (cartItemCount === 0) {
      // 0件ならバブル自体を削除
      if (cartCountBubble) cartCountBubble.remove();
      return;
    }
    if (cartCountBubble) {
      // 既存バブルがあれば数値だけ更新
      const cartItemCountElm = cartCountBubble.querySelector('span');
      if (cartItemCountElm) {
        cartItemCountElm.textContent = cartItemCount;
      }
    } else {
      // バブルがなければ新規作成
      const newBubble = document.createElement('div');
      newBubble.className = 'cart-count-bubble';
      const newSpan = document.createElement('span');
      newSpan.setAttribute('aria-hidden', 'true');
      newSpan.textContent = cartItemCount;
      newBubble.appendChild(newSpan);
      cartItemCountWrap.appendChild(newBubble);
    }
  }
  reDirectCartPage() {
    window.location.href = '/cart';
  }
}
/* =================================================================== */

export class AjaxSectionUpdater {
  constructor(requestUrl) {
    this.requestUrl = requestUrl;
  }
  async init() {
    try {
      const response = await _retryOnceOnFetchFailure(() => themeRequest({
        target: 'section-ajax-update',
        scenarios: CART_SECTION_ERROR_SCENARIOS,
        requestContext: { requestUrl: this.requestUrl },
        request: () => fetch(this.requestUrl),
      }));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      await this.updatePageContent(data);
    } catch (error) {
      console.error("Error fetching updated sections:", error);
      _notifyCriticalSectionCommunicationError(
        'my-utilities.js:AjaxSectionUpdater.init',
        'Section/Ajax update failed',
        error,
        { requestUrl: this.requestUrl },
        'section-ajax-update'
      );
    }
  }
  async updatePageContent(parsedData) {
    // console.log(parsedData);
    CustomLineItems.syncFromSections(parsedData);
    const updatePromises = Object.keys(parsedData).map(async (key) => {
      const updatedContent = parsedData[key];
      const targetElement = document.querySelector(`#${key}`);
      if (targetElement && updatedContent) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = updatedContent;
        const innerElement = tempDiv.querySelector(`#${key}`);
        if (innerElement) {
          targetElement.replaceWith(innerElement);
        } else {
          console.error(`ID "${key}" not found in the provided updated content.`);
        }
      } else {
        console.error(`Target element or updated content for ${key} not found.`);
      }
    });
    await Promise.all(updatePromises);
  }
}
/* =================================================================== */

export class Counter {
  constructor(max, onComplete = () => { }) {
    this.max = max; // ミリ秒数で指定された最大値
    this.currentValue = 0;
    this.intervalId = null;
    this.onComplete = onComplete; // カウント終了後に実行する関数
  }
  // カウントアップの共通ロジック
  countUp() {
    this.currentValue += 100; // 0.1秒（100ミリ秒）ごとにカウントアップ
    if (this.currentValue >= this.max) {
      this.currentValue = 0; // リセット
      this.onComplete(); // カウント終了後の処理を実行
    }
  }
  // カウントスタート
  startCountUp() {
    this.stop(); // 既にカウント中なら停止
    this.intervalId = setInterval(() => this.countUp(), 100);
  }
  // カウント停止
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
  // カウント再開
  resumeCountUp() {
    if (!this.intervalId) { // カウントが停止している場合のみ再開
      this.intervalId = setInterval(() => this.countUp(), 100);
    }
  }
  // カウントリセット
  reset() {
    this.stop(); // カウントを停止
    this.currentValue = 0; // 値をリセット
  }
}
/* =================================================================== */

// class CusomLineitems extends HTMLElement {
export class SectionJsonScripts {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.sectionHtml = "";
  }
  async update() {
    try {
      const data = await _retryOnceOnFetchFailure(() => themeRequest({
        target: 'section-json-read',
        scenarios: CART_SECTION_ERROR_SCENARIOS,
        requestContext: { sectionId: this.sectionId },
        request: () => fetch(`/?sections=${this.sectionId}`, { cache: "no-store" }),
      })).then((response) => response.json());
      this.sectionHtml = data[this.sectionId] || "";
      return this.sectionHtml;
    } catch (error) {
      console.error("SectionJsonScriptsのデータ取得に失敗:", error);
      this.sectionHtml = "";
      _notifyCriticalSectionCommunicationError(
        'my-utilities.js:SectionJsonScripts.update',
        'Section JSON fetch failed',
        error,
        { sectionId: this.sectionId },
        'section-json-fetch'
      );
      return "";
    }
  }
  getJson(scriptId) {
    return SectionJsonScripts.parseScriptJson(this.sectionHtml, scriptId);
  }
  static parseScriptJson(sectionHtml, scriptId) {
    if (!sectionHtml) {
      return null;
    }
    try {
      const doc = new DOMParser().parseFromString(String(sectionHtml), 'text/html');
      const safeId = String(scriptId).replace(/"/g, '\\"');
      const el = doc.querySelector(`script[id="${safeId}"][type="application/json"]`);
      if (!el) {
        return null;
      }
      return JSON.parse(String(el.textContent || '').trim());
    } catch (error) {
      console.warn('[SectionJsonScripts] parseScriptJson error', { scriptId }, error);
      return null;
    }
  }
}
//   constructor() {
//     super();
//   }
//   connectedCallback() {
//   }
//   
//   aggregateData(updateLine, updateQuantity) {
//     const data = this.jsonData;
//     // updateLine と updateQuantity の両方が指定されている場合は、該当の要素を更新する
//     if (updateLine !== undefined && updateQuantity !== undefined) {
//       if (data[updateLine]) {
//         const parentItem = data[updateLine];
//         const prevQuantity = parentItem.quantity; // 変更前の数量を保存
//         parentItem.quantity = updateQuantity;
//         // 親アイテムの数量変更に応じて、子アイテムの数量を再計算
//         if (parentItem.bundle_items) {
//           parentItem.bundle_items.forEach(bundleItem => {
//             const unit = bundleItem.quantity / prevQuantity; // 1単位の数量
//             bundleItem.quantity = unit * updateQuantity; // 新しい数量を設定
//           });
//         }
//       }
//     }
//     // console.log("処理前データ:", data); // ここで確認
//     const map = new Map();
//     data.forEach(item => {
//       this._addToMap(map, { ...item });
//       if (item.bundle_items) {
//         item.bundle_items.forEach(bundleItem => this._addToMap
// (map, { ...bundleItem }, item.quantity));
//       }
//     });
//     // console.log("処理後データ:", Array.from(map.values())); // ここで確認
//     return Array.from(map.values());
//   }
//   // 在庫超過した ID を取得
//   getExceededItems(updateLine, updateQuantity) {
//     return this.aggregateData(updateLine, updateQuantity)
//       .filter(item => item.quantity > item.inventory_quantity);
//   }
//   _addToMap(map, item, parentQuantity) {
//     if (map.has(item.id)) {
//       let plusQuantity = null;
//       if (parentQuantity) {
//         // セット子品番なら、親商品分数量調整
//         plusQuantity = item.quantity;
//       } else {
//         plusQuantity = item.quantity
//       }
//       let existing = map.get(item.id);
//       existing.quantity += plusQuantity;
//     } else {
//       map.set(item.id, { ...item });
//     }
//   }
// }
export class CustomLineItems {
  constructor() {
    if (!Array.isArray(CustomLineItems.latestJsonData)) {
      CustomLineItems.latestJsonData = [];
    }
  }
  static isFreshCache(maxAgeMs = 1500) {
    return Array.isArray(this.latestJsonData)
      && this.latestJsonData.length > 0
      && Number.isFinite(this.latestSyncAt)
      && Date.now() - this.latestSyncAt < maxAgeMs;
  }
  async update(options = {}) {
    const forceRefresh = options?.force === true;
    const caller = String(options?.caller || '').trim() || 'unknown';
    if (!forceRefresh && CustomLineItems.isFreshCache()) {
      _customApiDebugLog("[CustomLineItems] reuse fresh v2 cache", {
        lineCount: this.data.length,
      });
      return this.data;
    }
    try {
      const response = await _retryOnceOnFetchFailure(() => themeRequest({
        target: 'cart-line-items-read',
        scenarios: CART_STATE_ERROR_SCENARIOS,
        request: () => fetch('/cart.js', { cache: 'no-store' }),
      }));
      if (!response.ok) throw new Error(`Failed to fetch cart data: ${response.status}`);
      const cart = await response.json();
      if (!Array.isArray(cart?.items)) {
        throw new TypeError('Cart line items response is invalid');
      }
      CustomLineItems.syncFromRawLines(cart.items);
      _customApiDebugLog("[CustomLineItems] fetched", {
        lineCount: this.data.length,
      });
      return this.data;
    } catch (error) {
      console.error("CustomLineItemsのデータ取得に失敗:", error);
      throw error;
    }
  }
  get data() {
    return Array.isArray(CustomLineItems.latestJsonData)
      ? CustomLineItems.latestJsonData
      : [];
  }
  static syncFromSections(parsedData) {
    const nextData = this.parseSectionsRawLines(parsedData);
    if (nextData) {
      this.latestJsonData = nextData;
      this.latestSyncAt = Date.now();
    }
  }
  static syncFromRawLines(rawLines) {
    if (Array.isArray(rawLines)) {
      this.latestJsonData = rawLines;
      this.latestSyncAt = Date.now();
    }
  }
  static parseSectionsRawLines(parsedData) {
    try {
      if (parsedData?.["my-cart-validation"]) {
        const rawLines = SectionJsonScripts.parseScriptJson(
          parsedData["my-cart-validation"],
          "my-cart-json-lines"
        )?.lines;
        if (Array.isArray(rawLines)) {
          return rawLines;
        }
      }
    } catch (error) {
      console.error("CustomLineItems JSON parse error:", error);
    }
    return null;
  }
  
  aggregateData(updateLine, updateQuantity) {
    const data = this.data.map((item) => ({
      ...item,
      bundle_items: Array.isArray(item?.bundle_items)
        ? item.bundle_items.map((bundleItem) => ({ ...bundleItem }))
        : item?.bundle_items,
    }));
    if (updateLine !== undefined && updateQuantity !== undefined) {
      if (data[updateLine]) {
        const parentItem = data[updateLine];
        const prevQuantity = parentItem.quantity;
        parentItem.quantity = updateQuantity;
        if (parentItem.bundle_items) {
          parentItem.bundle_items.forEach(bundleItem => {
            const unit = bundleItem.quantity / prevQuantity;
            bundleItem.quantity = unit * updateQuantity;
          });
        }
      }
    }
    const map = new Map();
    data.forEach(item => {
      this._addToMap(map, { ...item });
      if (item.bundle_items) {
        item.bundle_items.forEach(bundleItem => this._addToMap(map, { ...bundleItem }, item.quantity));
      }
    });
    return Array.from(map.values());
  }
  getExceededItems(updateLine, updateQuantity) {
    return this.aggregateData(updateLine, updateQuantity)
      .filter(item => item.quantity > item.inventory_quantity);
  }
  _addToMap(map, item, parentQuantity) {
    if (map.has(item.id)) {
      let plusQuantity = parentQuantity ? item.quantity : item.quantity;
      let existing = map.get(item.id);
      existing.quantity += plusQuantity;
    } else {
      map.set(item.id, { ...item });
    }
  }
}
/* =================================================================== */

export class CustomApiCommunicator {
  static normalizeLineProperties(properties) {
    if (!properties || typeof properties !== 'object') {
      return undefined;
    }
    const entries = Object.entries(properties).filter(([, value]) => value !== '' && value !== null && value !== undefined);
    if (entries.length === 0) {
      return undefined;
    }
    return Object.fromEntries(entries);
  }
  static normalizeCartLines(lines) {
    if (!Array.isArray(lines)) {
      return [];
    }
    return lines.reduce((normalizedLines, line) => {
      if (!line) {
        return normalizedLines;
      }
      const variantId = line.variantId ?? line.variant_id ?? line.id;
      const quantity = Number(line.quantity ?? 0);
      if (!variantId || quantity <= 0) {
        return normalizedLines;
      }
      const normalizedLine = {
        variantId: String(variantId),
        quantity,
      };
      const properties = this.normalizeLineProperties(line.properties);
      if (properties) {
        normalizedLine.properties = properties;
      }
      normalizedLines.push(normalizedLine);
      return normalizedLines;
    }, []);
  }
  static getEmbeddedCartLines() {
    const scriptElement = document.getElementById(CART_STATE_ELEMENT_IDS.LINES);
    if (!scriptElement || !scriptElement.textContent) {
      return null;
    }
    try {
      const cart = JSON.parse(scriptElement.textContent);
      return this.normalizeCartLines(cart?.lines);
    } catch (parseError) {
      console.error('Failed to parse embedded cart JSON:', parseError);
      return [];
    }
  }
  static async fetchCartLines() {
    const embeddedLines = this.getEmbeddedCartLines();
    if (embeddedLines !== null) {
      return embeddedLines;
    }
    try {
      const response = await themeRequest({
        target: 'cart-state-read',
        request: () => fetch(`/cart.js`),
      });
      if (!response.ok) {
        console.warn('Failed to fetch cart JSON:', response.status);
        return [];
      }
      const cart = await response.json();
      return this.normalizeCartLines(cart?.items);
    } catch (error) {
      console.error('Error fetching cart lines:', error);
      return [];
    }
  }
  static async fetchLiveCart() {
    try {
      const response = await themeRequest({
        target: 'cart-state-read',
        request: () => fetch(`/cart.js`),
      });
      if (!response.ok) {
        console.warn('Failed to fetch live cart JSON:', response.status);
        return null;
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching live cart:', error);
      return null;
    }
  }
  static sendDiagnostic(config, logLabel = 'Diagnostic') {
    (async () => {
      try {
        const validationResult = config.validationResult || null;
        const liveCart = config.cart || await CustomApiCommunicator.fetchLiveCart();
        const lines = CustomApiCommunicator.normalizeCartLines(config.lines || validationResult?.lines || liveCart?.items);
        const itemCount = Number(liveCart?.item_count ?? lines.reduce((total, line) => total + line.quantity, 0));
        if (itemCount <= 1 || lines.length === 0) {
          _customApiDebugLog(`skip ${logLabel.toLowerCase()} diagnostic`, {
            pagePath: config.pagePath || window.location.pathname,
            itemCount,
            linesLength: lines.length,
          });
          return;
        }
        const clientCtx = getClientContext ? getClientContext() : {};
        const customer = {
          id: config.customer?.id ?? null,
          accepts_marketing: config.customer?.accepts_marketing ?? false,
        };
        const payload = {
          action: 'cart',
          customer,
          cart_limit_groups: config.cart_limit_groups || [],
          novelties: config.novelties || [],
          attributes: config.attributes || {},
          cart: {
            lines: lines
          },
          validation: validationResult ? {
            ok: validationResult.ok,
            lines: validationResult.lines || [],
            operations: validationResult.operations || [],
          } : undefined,
          pagePath: config.pagePath || window.location.pathname,
          clientCtx
        };
        _customApiDebugLog('sendDiagnostic payload', payload);
        themeRequest({
          target: 'cart-log',
          scenarios: [],
          requestContext: { body: payload },
          request: () => fetch('/apps/custom-api', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload),
            keepalive: true
          }),
        }).catch((err) => {
          console.debug(`${logLabel} diagnostic sent (fire-and-forget)`, err);
        });
      } catch (error) {
        console.error(`Error sending ${logLabel.toLowerCase()} diagnostic:`, error);
      }
    })();
  }
}

export function getClientContext() {
  const userAgent = navigator.userAgent;
  let os = 'unknown';
  if (/windows/i.test(userAgent)) os = 'Windows';
  else if (/macintosh|mac os x/i.test(userAgent)) os = 'Mac';
  else if (/android/i.test(userAgent)) os = 'Android';
  else if (/iphone|ipad|ipod/i.test(userAgent)) os = 'iOS';
  else if (/linux/i.test(userAgent)) os = 'Linux';
  let browser = 'unknown';
  if (/chrome|crios/i.test(userAgent) && !/edge|edg|opr|opera/i.test(userAgent)) browser = 'Chrome';
  else if (/firefox|fxios/i.test(userAgent)) browser = 'Firefox';
  else if (/safari/i.test(userAgent) && !/chrome|crios|android/i.test(userAgent)) browser = 'Safari';
  else if (/edg|edge/i.test(userAgent)) browser = 'Edge';
  else if (/opr|opera/i.test(userAgent)) browser = 'Opera';
  const device = getDevice();
  const cookieEnabled = navigator.cookieEnabled;
  const referrer = document.referrer || '';
  const clientContext = {
    os,
    browser,
    device,
    userAgent,
    cookieEnabled,
    referrer,
  };
  return clientContext;
}
